import { defineStore } from 'pinia';
import { formatDisplayDateDE } from '@/utils/date';

export const useSharedStore = defineStore({
  id: 'shared',
  state: () => ({
    ignoreBrowserReroute: false,
    routeLoading: false,
    currentMapCenter: null as null | [number, number],
  }),
  getters: {},
  actions: {
    formatPrice({
      countryLocale,
      currency,
      amount,
    }: {
      countryLocale: string;
      currency: string;
      amount: number;
    }) {
      return new Intl.NumberFormat(countryLocale, {
        style: 'currency',
        currency: currency.toUpperCase(),
        signDisplay: 'never',
        currencyDisplay: 'symbol',
      }).format(amount / 100);
    },
    formatDate(date: string) {
      return formatDisplayDateDE(date);
    },
    createDebounce() {
      let timeout: ReturnType<typeof setTimeout> | undefined;
      return function (fnc: () => void, delayMs: number) {
        clearTimeout(timeout);
        timeout = setTimeout(() => {
          fnc();
        }, delayMs || 500);
      };
    },
    log(input: any, logMessage?: string): void {
      console.log(logMessage, input);
    },
    validEmailStructure(email: string) {
      // const emailAddress: string = 'laurenz.morbe@gmail.com'
      const emailTestRegex = /^[a-zA-Z0-9._%+-]+@[a-zA-Z0-9.-]+\.[a-zA-Z]{2,}$/;
      if (!emailTestRegex.test(email)) return false;
      const addressParts: string[] = email.split('@');
      // console.log(addressParts)
      if (addressParts.length > 2) return false; // only one '@' is allowed
      if (addressParts[0].length > 64) return false; // character limit for prefix is 64
      if (addressParts[1].length > 255) return false; // character limit for suffix is 255
      return true;
    },
    validNewPassword(password: string): boolean {
      if (password.length < 8) return false;
      const hasNumberRegex = /(?=.*[0-9])/;
      if (!hasNumberRegex.test(password)) return false;
      const hasUppercaseRegex = /(?=.*[A-Z])/;
      if (!hasUppercaseRegex.test(password)) return false;
      const hasLowercaseRegex = /(?=.*[a-z])/;
      if (!hasLowercaseRegex.test(password)) return false;
      return true;
    },
    randomNumber({ min, max }: { min: number; max: number }) {
      min = Math.ceil(min);
      max = Math.floor(max);
      return Math.floor(Math.random() * (max - min + 1)) + min;
    },
    ApiRequest,
  },
});
import { useAuthStore } from '@/stores/auth';
import router, { loginScreenForRoute } from '@/router';
import * as Sentry from '@sentry/vue';

interface ApiRequestParameters<B = any> {
  url: string;
  method?: 'GET' | 'POST' | 'PUT' | 'DELETE';
  body?: B;
  options?: {
    useAuthorization?: boolean;
    responseType?: 'string' | 'json' | 'blob';
    headers?: Array<{ name: string; value: string }>;
    keepAlive?: boolean;
    // Opt in to fetch-level retry on TypeError (network failures). Off by
    // default — set only on call sites where duplicate requests are safe
    // and the UX benefit outweighs the wait (e.g. fridge unlock on flaky
    // mobile networks). See list in call sites.
    retryOnNetworkError?: boolean;
    onUploadProgress?: (percent: number) => void;
  };
}
interface ApiRequestOptions {
  method: 'GET' | 'POST' | 'PUT' | 'DELETE';
  headers: Headers;
  body: undefined | string | FormData;
  keepAlive?: boolean;
}

export async function ApiRequest<
  T extends Record<string, unknown> | string | Blob | object,
  E extends object = { message: string },
  B = any,
>({ url, method, body, options }: ApiRequestParameters<B>) {
  const res = await ApiRequestRaw({ url, method, body, options });

  //retry  flow due to expired jtw
  const isJwtExpired =
    res.status === 401 && (await res.clone().json()).message === 'jwt expired';
  if (!isJwtExpired) return await handleRes<T>(res, options);

  const tokenRefreshOptions = {
    useAuthorization: false,
  };
  const tokenResponse = await ApiRequestRaw({
    url: '/rest/auth/token-refresh',
    options: tokenRefreshOptions,
  });

  //if token refresh fails => reroute to login page. Manual login required.
  const isRefreshTokenExpired =
    !tokenResponse.ok && tokenResponse.status !== 500;
  if (isRefreshTokenExpired) {
    const importedRouter = router;
    const currentRoute = router.currentRoute.value;
    importedRouter.push({
      name: loginScreenForRoute(currentRoute),
      query: {
        redirect: currentRoute.path,
      },
    });
  }

  const tokenResponseHandled = await handleRes<string>(
    tokenResponse,
    tokenRefreshOptions,
  );
  const authStore = useAuthStore();
  authStore.setAuthToken(tokenResponseHandled.value);
  const retryOriginal = await ApiRequestRaw({ url, method, body, options });
  return await handleRes<T>(retryOriginal, options);
}
// Retry budget for fetch-level network failures (TypeError). Individual
// requests are NOT timed out — Selfly-backed endpoints can legitimately
// take ~60s on a slow day, so we only cap total wall-clock time across
// attempts. See discussion in logs of expo failures around 2026-04-21.
const API_RETRY_TOTAL_BUDGET_MS = 120_000;
const API_RETRY_MAX_ATTEMPTS = 5;
const API_RETRY_BACKOFFS_MS = [300, 600, 1200, 2400];

function sleep(ms: number): Promise<void> {
  return new Promise((resolve) => setTimeout(resolve, ms));
}

export async function ApiRequestRaw<
  T extends Record<string, unknown> | string,
  E extends object = { message: string },
  B = any,
>({ url, method, body, options }: ApiRequestParameters<B>) {
  const requestOptions: ApiRequestOptions = {
    method: method ?? 'POST',
    headers: new Headers(),
    body: undefined,
    keepAlive: undefined,
  };
  //headers
  if (body) {
    if (body instanceof FormData) {
      requestOptions.body = body;
    } else {
      requestOptions.headers.append('Content-Type', 'application/json');
      requestOptions.body = JSON.stringify(body);
    }
  }
  const useAuth = options?.useAuthorization ?? true;
  if (authHeader() && useAuth) {
    requestOptions.headers.append('Authorization', `Bearer ${authHeader()}`);
  }
  if (options?.headers) {
    options.headers.forEach((header) => {
      requestOptions.headers.append(header.name, header.value);
    });
  }
  if (options?.keepAlive !== undefined) {
    requestOptions.keepAlive = options.keepAlive;
  }

  if (options?.onUploadProgress) {
    return await xhrApiRequestRaw({ url, method, body, options, requestOptions });
  }

  // Retry is opt-in per call site. keepAlive is incompatible with retry
  // (post-unload attempts would be dropped by the browser), so it
  // overrides even when retryOnNetworkError is set.
  if (!options?.retryOnNetworkError || requestOptions.keepAlive) {
    return await fetch(url, requestOptions);
  }

  const start = Date.now();
  let lastError: unknown;

  for (let attempt = 1; attempt <= API_RETRY_MAX_ATTEMPTS; attempt++) {
    try {
      return await fetch(url, requestOptions);
    } catch (error) {
      lastError = error;
      // Only retry fetch-level network failures (TypeError from the
      // browser's fetch implementation). HTTP 4xx/5xx come back as a
      // resolved Response and are handled by callers.
      if (!(error instanceof TypeError)) throw error;

      const elapsed = Date.now() - start;
      Sentry.addBreadcrumb({
        category: 'fetch.retry',
        level: 'warning',
        message: `ApiRequestRaw attempt ${attempt}/${API_RETRY_MAX_ATTEMPTS} failed`,
        data: {
          url,
          attempt,
          elapsedMs: elapsed,
          errorMessage: error.message,
        },
      });

      if (attempt >= API_RETRY_MAX_ATTEMPTS) break;

      const backoff = API_RETRY_BACKOFFS_MS[attempt - 1];
      if (elapsed + backoff >= API_RETRY_TOTAL_BUDGET_MS) break;

      await sleep(backoff);
    }
  }

  throw lastError;
}

function xhrApiRequestRaw<B>({
  url,
  options,
  requestOptions,
}: ApiRequestParameters<B> & { requestOptions: ApiRequestOptions }): Promise<Response> {
  return new Promise((resolve, reject) => {
    const xhr = new XMLHttpRequest();
    xhr.open(requestOptions.method, url);

    requestOptions.headers.forEach((value, name) => {
      xhr.setRequestHeader(name, value);
    });

    xhr.upload.addEventListener('progress', (event) => {
      if (event.lengthComputable && options?.onUploadProgress) {
        options.onUploadProgress(
          Math.min(100, Math.round((event.loaded / event.total) * 100)),
        );
      }
    });

    xhr.addEventListener('load', () => {
      const responseHeaders = new Headers();
      const rawHeaders = xhr.getAllResponseHeaders().trim();
      if (rawHeaders) {
        rawHeaders.split(/[\r\n]+/).forEach((line) => {
          const parts = line.split(': ');
          const name = parts.shift();
          const value = parts.join(': ');
          if (name) {
            responseHeaders.append(name, value);
          }
        });
      }

      resolve(
        new Response(xhr.responseText, {
          status: xhr.status,
          statusText: xhr.statusText,
          headers: responseHeaders,
        }),
      );
    });

    xhr.addEventListener('error', () => {
      reject(new TypeError('Network request failed'));
    });

    xhr.addEventListener('abort', () => {
      reject(new DOMException('The operation was aborted.', 'AbortError'));
    });

    xhr.send(requestOptions.body ?? null);
  });
}

export type FileProgress = {
  completed: number;
  total: number;
};

type NdjsonStreamEvent<T> =
  | { type: 'progress'; completed: number; total: number }
  | { type: 'complete'; data: T };

export async function apiRequestFormDataWithNdjsonStream<
  T extends Record<string, unknown> | object,
>({
  url,
  body,
  onFileProgress,
}: {
  url: string;
  body: FormData;
  onFileProgress?: (progress: FileProgress) => void;
}): Promise<T> {
  return new Promise((resolve, reject) => {
    const xhr = new XMLHttpRequest();
    xhr.open('POST', url);

    const token = authHeader();
    if (token) {
      xhr.setRequestHeader('Authorization', `Bearer ${token}`);
    }

    let parsedLength = 0;
    let lineBuffer = '';
    let settled = false;

    const settleResolve = (value: T) => {
      if (settled) return;
      settled = true;
      resolve(value);
    };

    const settleReject = (error: Error) => {
      if (settled) return;
      settled = true;
      reject(error);
    };

    const handleProgressLine = (line: string) => {
      if (!line.trim()) return;

      const event = JSON.parse(line) as NdjsonStreamEvent<T>;
      if (event.type === 'progress') {
        onFileProgress?.({
          completed: event.completed,
          total: event.total,
        });
      } else if (event.type === 'complete') {
        settleResolve(event.data as T);
      }
    };

    const consumeResponseText = (isFinal: boolean) => {
      const chunk = xhr.responseText.slice(parsedLength);
      parsedLength = xhr.responseText.length;
      lineBuffer += chunk;

      const lines = lineBuffer.split('\n');
      lineBuffer = isFinal ? '' : (lines.pop() ?? '');

      for (const line of lines) {
        handleProgressLine(line);
      }

      if (isFinal && lineBuffer.trim()) {
        handleProgressLine(lineBuffer);
      }
    };

    xhr.addEventListener('readystatechange', () => {
      if (xhr.readyState === XMLHttpRequest.LOADING) {
        consumeResponseText(false);
      }
    });

    xhr.addEventListener('load', () => {
      if (xhr.status >= 400) {
        let message = 'Something went wrong';
        try {
          const error = JSON.parse(xhr.responseText) as { message?: string };
          message = error.message ?? message;
        } catch {
          message = xhr.responseText || message;
        }
        settleReject(new Error(message));
        return;
      }

      consumeResponseText(true);
      if (!settled) {
        settleReject(new Error('Upload finished without a complete response'));
      }
    });

    xhr.addEventListener('error', () => {
      settleReject(new TypeError('Network request failed'));
    });

    xhr.addEventListener('abort', () => {
      settleReject(new DOMException('The operation was aborted.', 'AbortError'));
    });

    xhr.send(body);
  });
}

// helper functions

function authHeader() {
  // return auth header with jwt if user is logged in and request is to the api url
  const { authToken } = useAuthStore();
  const isLoggedIn = !!authToken;
  if (isLoggedIn) {
    return authToken;
  } else {
    return false;
  }
}

export class ParsingError extends Error {
  public reason: string;
  public row: number;
  public column: number;
  constructor(message: string, reason: string, row: number, column: number) {
    super(message);
    this.reason = reason;
    this.row = row;
    this.column = column;
  }
}

export async function handleRes<
  T extends Record<string, unknown> | string | Blob | object,
>(
  res: Response,
  options: ApiRequestParameters['options'],
): Promise<{ value: T; response: Response }> {
  const responseType = options?.responseType ?? 'string';
  if (!res.ok) {
    const errorText = await res.text();
    let error: {
      message?: string;
      errorType?: string;
      reason?: string;
      row?: Record<string, string>;
      column?: string;
    };
    try {
      error = JSON.parse(errorText) as typeof error;
    } catch {
      throw new Error(errorText || 'Something went wrong');
    }
    if (error.errorType === 'ParsingError') {
      console.log('message:', error.message);
      console.log('reason:', error.reason);
      throw new ParsingError(
        error.message,
        error.reason,
        error.row,
        error.column,
      );
    }
    throw new Error(error.message ?? 'Something went wrong');
  }
  let value;
  switch (responseType) {
    case 'string':
      value = await res.text();
      break;
    case 'json':
      value = await res.json();
      break;
    case 'blob':
      value = await res.blob();
      break;
  }

  return {
    value,
    response: res,
  };
}

export async function googleLog(
  message: string,
  severity: 'debug' | 'warn' | 'info' | 'error' | 'fatal' = 'info',
) {
  const auth = useAuthStore();
  const type = auth.authToken
    ? auth.getUserRoleFromToken(auth.authToken)
    : undefined;
  return await ApiRequest<string>({
    method: 'POST',
    url: '/rest/google_alert/create',
    body: {
      basic_user_id: auth.id ?? undefined,
      basic_user_type: type,
      severity,
      message,
    },
  });
}
export async function createSessionStepMetric(
  step: string,
  data?: {
    user_id?: string | null;
    sent_from?: string;
    sent_at?: string;
    reset_session?: boolean;
  },
) {
  let session_id = localStorage.getItem('session_id');
  if (!session_id || session_id === '') {
    session_id = crypto.randomUUID();
    localStorage.setItem('session_id', session_id);
  }
  data = data || {};
  if (!data.sent_from) {
    const userAgent = navigator?.userAgent || 'Unknown User Agent';
    const platform = navigator?.platform || 'Unknown Platform';
    const language = navigator?.language || 'Unknown Language';
    data.sent_from = `${userAgent}; Platform: ${platform}; Language: ${language}`;
  }
  if (!data.sent_at) {
    data.sent_at = new Date().toISOString();
  }
  if (!data.user_id) {
    data.user_id = useAuthStore().id;
  }
  if (data.reset_session) {
    localStorage.removeItem('session_id');
  }
  return await ApiRequest<string>({
    method: 'POST',
    url: '/rest/metrics/create/session-step',
    body: {
      session_id,
      step,
      user_id: data.user_id,
      sent_from: data.sent_from,
      sent_at: data.sent_at,
    },
  });
}
